import React, { useCallback, useEffect, useRef, useState } from "react"
import { Image, View, StyleSheet, ActivityIndicator } from "react-native"
import { connect } from "react-redux"
import { theme } from "@common/theme/theme"
import { useApiStatus } from "@common/hooks/useApiStatus"
import { apiRequest } from "@data/redux/actions/api"
import { activitiesService } from "@data/services/activitiesService"
import FilledButtonM from "@common/components/button/FilledButtonM"
import { hasRole } from "@data/utility/ability"
import { ImageViewerModal } from "@common/camera/CameraImagesM"
import { ErrorBanner } from "@common/components/alert/ErrorBanner"
import FeedbackButton from "@common/components/feedback/FeedbackButton"
import TextM from "@common/components/text/TextM"

const MaterialExplanationDisplay = (props) => {
    const {
        material,
        style = {},
        user,
        inline = false,
        headless = false,
        onStateChange,
    } = props
    const materialId = material?.id
    const [visualExplanationUrl, setVisualExplanationUrl] = useState(
        material?.visual_explanation && material?.visual_explanation?.length > 0
            ? material.visual_explanation[0]
            : null
    )
    const [isPolling, setIsPolling] = useState(false)
    const [hasTimedOut, setHasTimedOut] = useState(false)
    const [showModal, setShowModal] = useState(false)
    const [errorMessage, setErrorMessage] = useState(null)
    const [currentFeedback, setCurrentFeedback] = useState(null)
    const intervalRef = useRef()
    const timeoutRef = useRef()

    // Only show explanation features for tutors and if material has a statement
    const isTutor = hasRole(user, "tutor")
    const hasStatement =
        (material?.statement && material.statement.trim().length > 0) ||
        (material?.transcript && material.transcript.trim().length > 0)
    const materialSkills = material?.material_skills
    const hasSkills =
        typeof materialSkills === "string"
            ? materialSkills.trim().length > 0
            : Array.isArray(materialSkills) && materialSkills.length > 0
    const canViewExplanation = isTutor && hasStatement && hasSkills

    // Handle generation response
    useApiStatus(
        activitiesService.generateVisualExplanation,
        null,
        false,
        () => {
            // Start polling after generation request
            setErrorMessage(null)
            setIsPolling(true)
        },
        (error) => {
            // Handle error from Redux state
            // The error object comes from state.api[serviceName].error
            if (error?.message) {
                setErrorMessage(error.message)
            } else {
                setErrorMessage("Une erreur est survenue lors de la génération")
            }
        }
    )

    // Handle material fetch (polling) response
    useApiStatus(
        activitiesService.getMaterial,
        null,
        false,
        (data) => {
            const material = data?.data || data
            if (material?.id === materialId) {
                // Check if we have visual explanation
                if (
                    material?.visual_explanation &&
                    material?.visual_explanation?.length > 0
                ) {
                    setVisualExplanationUrl(material.visual_explanation[0])
                    // Stop polling
                    setIsPolling(false)
                    clearInterval(intervalRef.current)
                    clearTimeout(timeoutRef.current)
                }
            }
        },
        () => {
            // On error, continue polling
        },
        true // noIdle
    )

    // Handle feedback loading
    useApiStatus(
        activitiesService.getMaterialFeedback,
        null,
        false,
        (data) => {
            setCurrentFeedback(data?.data || data)
        },
        () => {
            // On error, set feedback to null
            setCurrentFeedback(null)
        },
        true
    )

    // Handle feedback submission
    useApiStatus(
        activitiesService.submitMaterialFeedback,
        null,
        true,
        (data) => {
            setCurrentFeedback(data?.data || data)
        },
        () => {
            // On error, show message handled by useApiStatus
        },
        true
    )

    // Update state when material prop changes
    useEffect(() => {
        const newUrl =
            material?.visual_explanation &&
            material?.visual_explanation?.length > 0
                ? material.visual_explanation[0]
                : null
        if (newUrl !== visualExplanationUrl) {
            setVisualExplanationUrl(newUrl)
        }
    }, [material?.visual_explanation])

    // Fetch feedback when visual explanation is available
    useEffect(() => {
        if (materialId && visualExplanationUrl && hasRole(user, "tutor")) {
            props.apiRequest(activitiesService.getMaterialFeedback, {
                material_id: materialId,
                feature_type: "visual_explanation",
            })
        }
    }, [materialId, visualExplanationUrl])

    // Polling effect
    useEffect(() => {
        if (isPolling && materialId) {
            // Set timeout to stop polling after 90 seconds
            timeoutRef.current = setTimeout(() => {
                setIsPolling(false)
                setHasTimedOut(true)
                clearInterval(intervalRef.current)
            }, 90000) // 90 seconds

            // Poll every 5 seconds
            intervalRef.current = setInterval(() => {
                props.apiRequest(activitiesService.getMaterial, {
                    id: materialId,
                })
            }, 5000)

            // Initial fetch
            props.apiRequest(activitiesService.getMaterial, { id: materialId })

            return () => {
                clearInterval(intervalRef.current)
                clearTimeout(timeoutRef.current)
            }
        }
    }, [isPolling, materialId])

    const handleFeedback = useCallback(
        (feedbackType) => {
            if (!materialId) return

            props.apiRequest(activitiesService.submitMaterialFeedback, {
                material_id: materialId,
                feedback_type: feedbackType,
                feature_type: "visual_explanation",
                comment: null,
            })
        },
        [materialId]
    )

    const handleGenerateClick = useCallback(() => {
        if (!visualExplanationUrl && !isPolling) {
            props.apiRequest(activitiesService.generateVisualExplanation, {
                material_id: materialId,
            })
        }
    }, [visualExplanationUrl, isPolling, materialId])

    const clearError = useCallback(() => setErrorMessage(null), [])

    // Expose internal state to parent in headless mode
    const prevStateRef = useRef({})
    useEffect(() => {
        if (onStateChange) {
            const newState = {
                visualExplanationUrl,
                isPolling,
                hasTimedOut,
                errorMessage,
                currentFeedback,
                handleGenerateClick,
                handleFeedback,
                clearError,
            }
            // Only call onStateChange if a data value actually changed
            const prev = prevStateRef.current
            if (
                prev.visualExplanationUrl !== newState.visualExplanationUrl ||
                prev.isPolling !== newState.isPolling ||
                prev.hasTimedOut !== newState.hasTimedOut ||
                prev.errorMessage !== newState.errorMessage ||
                prev.currentFeedback !== newState.currentFeedback
            ) {
                prevStateRef.current = newState
                onStateChange(newState)
            }
        }
    }, [
        visualExplanationUrl,
        isPolling,
        hasTimedOut,
        errorMessage,
        currentFeedback,
        handleGenerateClick,
        handleFeedback,
        clearError,
    ])

    // Early returns AFTER all hooks (to comply with Rules of Hooks)
    if (headless) return null
    if (!materialId || !canViewExplanation) return null

    const handleToggle = () => {
        if (visualExplanationUrl && !isPolling) {
            setShowModal(true)
        }
    }

    // Determine button state
    let buttonState = {
        label: "Générer visuel pour démarrer",
        icon: "image",
        loading: false,
        disabled: false,
    }

    if (isPolling) {
        buttonState = {
            label: "Génération du visuel...",
            loading: true,
            disabled: true,
        }
    } else if (hasTimedOut && !visualExplanationUrl) {
        buttonState = {
            label: "Visuel non disponible",
            disabled: true,
        }
    } else if (visualExplanationUrl) {
        buttonState = {
            label: "Visuel pour démarrer",
            icon: "image",
            loading: false,
            disabled: false,
        }
    }

    // Inline mode: show image directly, or generate button if no image yet
    if (inline) {
        return (
            <View style={[styles.inlineContainer, style]}>
                {errorMessage && (
                    <ErrorBanner
                        message={errorMessage}
                        dismissable={true}
                        onDismiss={() => setErrorMessage(null)}
                    />
                )}

                {visualExplanationUrl ? (
                    <>
                        <Image
                            source={{ uri: visualExplanationUrl }}
                            style={styles.inlineImage}
                            resizeMode="contain"
                        />
                        <View style={styles.inlineFeedbackRow}>
                            <FeedbackButton
                                onPress={() => handleFeedback("positive")}
                                isSelected={
                                    currentFeedback?.feedback_type ===
                                    "positive"
                                }
                                isPositive={true}
                                tooltipText="Visuel utile"
                                minimal={true}
                                size={{ icon: 16, text: 8 }}
                            />
                            <View style={{ width: 8 }} />
                            <FeedbackButton
                                onPress={() => handleFeedback("negative")}
                                isSelected={
                                    currentFeedback?.feedback_type ===
                                    "negative"
                                }
                                isPositive={false}
                                tooltipText="Visuel peu utile"
                                minimal={true}
                                size={{ icon: 16, text: 8 }}
                            />
                        </View>
                    </>
                ) : isPolling ? (
                    <View style={styles.inlinePlaceholder}>
                        <ActivityIndicator
                            size="large"
                            color={theme.colors.primary}
                        />
                        <TextM
                            fontSize={13}
                            color={theme.colors.grey}
                            style={{ marginTop: 12 }}
                        >
                            Génération du visuel...
                        </TextM>
                    </View>
                ) : hasTimedOut ? (
                    <View style={styles.inlinePlaceholder}>
                        <TextM fontSize={13} color={theme.colors.grey}>
                            Visuel non disponible
                        </TextM>
                    </View>
                ) : (
                    <View style={styles.inlinePlaceholder}>
                        <FilledButtonM
                            trackingId="material-explanation-generate-inline"
                            onPress={handleGenerateClick}
                            label="Générer le visuel"
                            icon="image"
                            color={theme.colors.primary}
                            style={styles.button}
                            contentStyle={styles.buttonContent}
                            labelStyle={styles.buttonLabel}
                        />
                    </View>
                )}
            </View>
        )
    }

    return (
        <View style={[styles.container, style]}>
            {errorMessage && (
                <ErrorBanner
                    message={errorMessage}
                    dismissable={true}
                    onDismiss={() => setErrorMessage(null)}
                />
            )}

            <FilledButtonM
                trackingId="material-explanation-display-button"
                onPress={() => {
                    if (!buttonState.disabled) {
                        if (!visualExplanationUrl) {
                            handleGenerateClick()
                        } else {
                            handleToggle()
                        }
                    }
                }}
                label={buttonState.label}
                icon={buttonState.loading ? null : buttonState.icon}
                loading={buttonState.loading}
                disabled={buttonState.disabled}
                mode="contained"
                color={
                    buttonState.disabled
                        ? theme.colors.grey
                        : theme.colors.primary
                }
                style={styles.button}
                contentStyle={styles.buttonContent}
                labelStyle={styles.buttonLabel}
            />

            {visualExplanationUrl && (
                <ImageViewerModal
                    visible={showModal}
                    onDismiss={() => setShowModal(false)}
                    imageIndex={0}
                    images={[visualExplanationUrl]}
                    headerLeftComponent={() => (
                        <View
                            style={{
                                flexDirection: "row",
                                alignItems: "center",
                            }}
                        >
                            <FeedbackButton
                                onPress={() => handleFeedback("positive")}
                                isSelected={
                                    currentFeedback?.feedback_type ===
                                    "positive"
                                }
                                isPositive={true}
                                tooltipText="Visuel utile"
                                minimal={true}
                                iconColor="#FFFFFF"
                                size={{ icon: 18, text: 8 }}
                            />
                            <View style={{ width: 8 }} />
                            <FeedbackButton
                                onPress={() => handleFeedback("negative")}
                                isSelected={
                                    currentFeedback?.feedback_type ===
                                    "negative"
                                }
                                isPositive={false}
                                tooltipText="Visuel peu utile"
                                minimal={true}
                                iconColor="#FFFFFF"
                                size={{ icon: 18, text: 8 }}
                            />
                        </View>
                    )}
                />
            )}
        </View>
    )
}

const styles = StyleSheet.create({
    container: {
        marginTop: 16,
        marginBottom: 8,
        paddingHorizontal: 16,
    },
    button: {
        height: 40,
        marginBottom: 0,
    },
    buttonContent: {
        height: 40,
    },
    buttonLabel: {
        fontSize: 13,
    },
    // Inline mode
    inlineContainer: {
        flex: 1,
    },
    inlineImage: {
        flex: 1,
        width: "100%",
    },
    inlinePlaceholder: {
        flex: 1,
        alignItems: "center",
        justifyContent: "center",
    },
    inlineFeedbackRow: {
        flexDirection: "row",
        justifyContent: "center",
        paddingVertical: 6,
    },
})

const mapDispatchToProps = {
    apiRequest,
}

export default connect(null, mapDispatchToProps)(MaterialExplanationDisplay)
